import React, { useEffect, useMemo, useRef, useState } from 'react';
import Flexbox from 'flexbox-react';
import {
    Button,
    Classes,
    FormGroup,
    InputGroup,
    Menu,
    MenuItem,
    Popover,
    PopoverInteractionKind,
    Position,
    Tooltip,
} from '@blueprintjs/core';
import { FilledButton } from '../buttons-20250224T111359Z-001/buttons';
import Cookies from 'js-cookie';
import axios from 'axios';
import "./userJobProfile.css"
import ToastStore from '../../stores/ToastStore';
import ConfirmationDialog from '../../views/ConfirmationDialog';
import ScrollableDropdown from '../shared/ScrollableDropdown';
import { Icon } from '@blueprintjs/core';
import EditIcon from "../../assets/editIcon.svg";
import DeleteIcon from "../../assets/Delete.svg";

const JOB_PROFILE_TABLE_STYLES = `
.table-row {
  line-height: 24px;
  transition: background-color 0.2s ease-in-out;
}
.table-row:hover {
  background-color: #f5f5f5;
}
.status-row {
  align-items: center;
  text-align: center;
  justify-content: center;
} 
.status-row span {
  padding: 8px 20px;
  border-radius: 50px;
  text-align: center;
} 
.title-row {
  text-decoration: none;
}
.ellipsis-text {
  max-width: 250px; 
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: inline-block;
} 
table.bp3-html-table thead th.table-header-text, 
tbody td.table-header-text,
tbody td.span.table-header-text {
  color: #4D4D4D;
  font-family: Roboto;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
.table-header-text {
  color: #4D4D4D;
  font-family: Roboto;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}
.goal-table-container {
  width: 100%;
  overflow-x: auto;
}
.goal-table-container table {
  width: 100%;
  border-collapse: collapse;
}

/* Custom confirm modal (no Blueprint Alert) */
.jp-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(16, 22, 26, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.jp-modal {
  width: 520px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 18px 48px rgba(16, 22, 26, 0.22);
  padding: 18px 18px 14px;
}
.jp-modal-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.jp-modal-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(219, 55, 55, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
}
.jp-modal-title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: #182026;
}
.jp-modal-subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  line-height: 18px;
  color: #5C7080;
}
.jp-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
}
.jp-btn {
  border: 1px solid #CED9E0;
  background: #fff;
  color: #182026;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
}
.jp-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.jp-btn-danger {
  border-color: #DB3737;
  background: #DB3737;
  color: #fff;
}
`;

const UsersJobProfile = () => {
    const fileInputRef = useRef(null);
    const [profiles, setProfiles] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [editingProfileId, setEditingProfileId] = useState(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const isEditMode = useMemo(() => !!editingProfileId, [editingProfileId]);

    const fetchJobProfiles = async () => {
        const baseUrl = Cookies.get('apiEnv');
        if (!baseUrl) return;

        setIsLoading(true);
        try {
            const response = await axios.get(`${baseUrl}/job_profiles`);
            if (Array.isArray(response.data)) {
                setProfiles(response.data);
            } else {
                setProfiles([]);
            }
        } catch (e) {
            const message =
                e?.response?.data?.error?.join?.(', ') ||
                e?.response?.data?.error ||
                'Unable to load job profiles';
            ToastStore?.showToast?.(message, 'danger');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await axios.get(
                `${Cookies.get("apiEnv")}/question_categories`
            );

            setCategories(response.data || []);
        } catch (error) {
            console.error(error);
        }
    };


    useEffect(() => {
        fetchJobProfiles();
        fetchCategories();
    }, []);

    const resetForm = () => {
        setEditingProfileId(null);
        setName('');
        setDescription('');
        setSelectedCategories([]);
        setError('');
    };

    const openCreateDrawer = () => {
        resetForm();
        setIsDrawerOpen(true);
    };

    const openEditDrawer = profile => {
        setEditingProfileId(profile.id);
        setName(profile.name || '');
        setDescription(profile.description || '');
        setSelectedCategories(profile.question_category_ids || []);
        setError('');
        setIsDrawerOpen(true); 
    };

    const closeDrawer = () => {
        setIsDrawerOpen(false);
        resetForm();
    };

    const openDeleteDialog = profile => {
        setDeleteTarget(profile);
        setIsDeleteDialogOpen(true);
    };

    const closeDeleteDialog = () => {
        setIsDeleteDialogOpen(false);
        setDeleteTarget(null);
    };

    const confirmDeleteJobProfile = async () => {
        const baseUrl = Cookies.get('apiEnv');
        if (!baseUrl) return;

        const profileId = deleteTarget?.id;
        if (!profileId) return;

        try {
            const response = await axios.delete(`${baseUrl}/job_profiles/${profileId}`);
            ToastStore?.showToast?.(response?.data?.message || 'Job profile deleted successfully', 'success');
            closeDeleteDialog();
            await fetchJobProfiles();
        } catch (e) {
            const message =
                e?.response?.data?.error?.join?.(', ') ||
                e?.response?.data?.error ||
                'Unable to delete job profile';
            ToastStore?.showToast?.(message, 'danger');
        }
    };

    const handleSubmit = async e => {
        if (e && e.preventDefault) e.preventDefault();
        if (!name.trim()) {
            setError('Job Profile is required');
            return;
        }
        const baseUrl = Cookies.get('apiEnv');
        if (!baseUrl) return;

        setIsSubmitting(true);
        try {
            console.log("selectedCategories", selectedCategories);
            const payload = {
                name: name.trim(),
                description: description.trim(),
                question_category_ids: selectedCategories
            };

            if (isEditMode) {
                const response = await axios.put(`${baseUrl}/job_profiles/${editingProfileId}`, payload);
                const updated = response?.data?.job_profile || response.data;
                setProfiles(prev => prev.map(p => (p.id === editingProfileId ? updated : p)));
                ToastStore?.showToast?.(response?.data?.message || 'Job profile updated', 'success');
            } else {
                const response = await axios.post(`${baseUrl}/job_profiles`, payload);
                ToastStore?.showToast?.(response?.data?.message || 'Job profile successfully created', 'success');
                await fetchJobProfiles();
            }

            closeDrawer();
        } catch (e2) {
            const message =
                e2?.response?.data?.error?.join?.(', ') ||
                e2?.response?.data?.error ||
                'Unable to save job profile';
            ToastStore?.showToast?.(message, 'danger');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUploadClick = (e) => {
        if (e && e.preventDefault) {
            e.preventDefault();
            e.stopPropagation();
        }
        fileInputRef.current?.click();
    };

    const handleDownloadSample = () => {
        setIsDownloading(true);

        const link = document.createElement("a");
        link.href = "/sample_profile_exports.xlsx";
        link.download = "sample_profile_exports.xlsx";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => {
            setIsDownloading(false);
        }, 800);
    };

    const buildUploadToastMessage = (data) => {
        const mainMessage = data?.message || "File uploaded successfully";
        const rowErrors = Array.isArray(data?.row_errors) ? data.row_errors : [];

        if (rowErrors.length === 0) {
            return mainMessage;
        }

        const formattedErrors = rowErrors.map(error => {
            const details = Array.isArray(error.errors) ? error.errors.join(', ') : error.errors;
            return `Row ${error.row}: ${details}`;
        });

        return [mainMessage, ...formattedErrors].join('\n');
    };

    const handleFileChange = async (e) => {

        const file = e.target.files?.[0];
        if (!file) return;

        const allowedExtensions = [
            "xls", "xlsx", "xlsm", "xlsb", "xlt", "xltx",
            "ods", "csv"
        ];

        const fileExt = file.name.split(".").pop().toLowerCase();

        if (!allowedExtensions.includes(fileExt)) {
            ToastStore?.showToast?.("Invalid file format", "danger");
            e.target.value = "";
            return;
        }

        const baseUrl = Cookies.get("apiEnv");

        if (!baseUrl) {
            ToastStore?.showToast?.("API base URL not found", "danger");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        try {
            setIsUploading(true);
            setIsLoading(true);

            const response = await axios.post(
                `${baseUrl}/job_profiles/import`,
                formData
            );

            const toastMessage = buildUploadToastMessage(response?.data || {});
            const toastIntent = Array.isArray(response?.data?.row_errors) && response.data.row_errors.length > 0
                ? 'warning'
                : response?.data?.success
                    ? 'success'
                    : 'danger';

            ToastStore?.showToast?.(toastMessage, toastIntent);

            await fetchJobProfiles();

        } catch (error) {
            const message =
                error?.response?.data?.error ||
                error?.response?.data?.message ||
                "File upload failed";

            ToastStore?.showToast?.(message, "danger");
        } finally {
            setIsUploading(false);
            setIsLoading(false);

            e.target.value = "";
        }
    };

    return (
        <Flexbox className="fixed-width-container" flexDirection="column" style={{ marginLeft: 0, paddingLeft: 30 }}>
            <style>{JOB_PROFILE_TABLE_STYLES}</style>
            <Flexbox
                justifyContent="space-between"
                alignItems="center"
                className="push"
                style={{ marginTop: 10 }}
            >
                <div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <Popover
                        position={Position.BOTTOM_RIGHT}
                        interactionKind={PopoverInteractionKind.CLICK}
                        portalContainer={document.body}
                        content={
                            <div
                                style={{
                                    borderRadius: "12px",
                                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                    border: "1px solid #e5e7eb",
                                    overflow: "hidden",
                                    background: "#fff",
                                    width: "260px",
                                }}
                            >
                                {/* Download */}
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "12px",
                                        padding: "12px 16px",
                                        alignItems: "center",
                                        cursor: "pointer",
                                    }}
                                    onClick={handleDownloadSample}
                                >
                                    <div
                                        style={{
                                            background: "#e0edff",
                                            color: "#2d72d9",
                                            borderRadius: "8px",
                                            padding: "8px",
                                            display: "flex",
                                        }}
                                    >
                                        <Icon icon="download" />
                                    </div>

                                    <div>
                                        <div style={{ fontWeight: 600 }}>Download Sample</div>
                                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                            Download a sample file for the import format
                                        </div>
                                    </div>
                                </div>

                                {/* Upload (highlighted like screenshot) */}
                                <div
                                    style={{
                                        display: "flex",
                                        gap: "12px",
                                        padding: "12px 16px",
                                        alignItems: "center",
                                        cursor: "pointer",
                                        background: "#f3f4f6",
                                    }}
                                    onClick={handleUploadClick}
                                >
                                    <div
                                        style={{
                                            background: "#dcfce7",
                                            color: "#16a34a",
                                            borderRadius: "8px",
                                            padding: "8px",
                                            display: "flex",
                                        }}
                                    >
                                        <Icon icon="upload" />
                                    </div>

                                    <div>
                                        <div style={{ fontWeight: 600 }}>Upload File</div>
                                        <div style={{ fontSize: "12px", color: "#6b7280" }}>
                                            Upload a file to import job profile data
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }
                    >
                        <Button
                            style={{
                                borderRadius: "10px",
                                color: "#2d72d9",
                                // backgroundColor: "rgba(45, 114, 217, 0.08)",
                                border: "1px solid rgba(45, 114, 217, 0.3)",
                                padding: "9px 18px",
                                fontWeight: 500,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                            icon="import"
                            rightIcon="caret-down"
                            text="Import Job Profiles"
                        />
                    </Popover>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        accept=".xls,.xlsx,.xlsm,.xlsb,.xlt,.xltx,.ods,.csv"
                        onChange={handleFileChange}
                    />

                    {/* Add Button */}
                    <FilledButton
                        text="+ Add job profile"
                        onClick={openCreateDrawer}
                    />
                </div>
            </Flexbox>

            <Flexbox flexDirection="column" className="push-10-t job-profile-table-container">
                <table
                    className="bp3-html-table bp3-html-table-bordered bp3-small"
                    style={{ border: '1px solid #e5e5e5', width: '100%' }}
                >
                    <thead style={{ backgroundColor: 'rgba(189, 195, 199, 0.10)', color: '#4D4D4D' }}>
                        <tr style={{ alignContent: 'center', fontSize: '16px', fontWeight: '400', lineHeight: 'normal' }}>
                            <th style={{ width: '25%', padding: '12px 12px' }}>Name</th>
                            <th style={{ padding: '12px 12px' }}>Description</th>
                            <th style={{ width: '35%', padding: '12px 12px' }}>Departments</th>
                            <th style={{ width: 180, padding: '12px 12px' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading && (
                            <tr>
                                <td colSpan={3} style={{ textAlign: 'center', padding: '16px', color: '#777' }}>
                                    Loading...
                                </td>
                            </tr>
                        )}
                        {profiles?.map(profile => (
                            <tr key={profile.id} style={{ height: 56, verticalAlign: 'middle' }}>
                                <td
                                    style={{
                                        verticalAlign: 'middle',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => openEditDrawer(profile)}
                                >
                                    <Tooltip portalContainer={document.body}
                                        content="Edit"
                                        position={Position.TOP}
                                        hoverOpenDelay={100}
                                        boundary="viewport"
                                        className="custom-bp-tooltip"
                                    >
                                        <div
                                            role="button"
                                            tabIndex={0}
                                            onClick={(e) => { e.stopPropagation(); openEditDrawer(profile); }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    openEditDrawer(profile);
                                                }
                                            }}
                                            onMouseEnter={(e) => (e.currentTarget.style.color = "#0000e1")}
                                            onMouseLeave={(e) => (e.currentTarget.style.color = "#000")}
                                            style={{
                                                cursor: "pointer",
                                                color: "#000",
                                                outline: "none",
                                            }}
                                            aria-label={`Edit job profile ${profile.name}`}
                                        >
                                            {profile?.name}
                                        </div>
                                    </Tooltip>
                                </td>
                                <td style={{ color: '#555', verticalAlign: 'middle' }}>{profile?.description || "-"}</td>
                                <td style={{ color: '#555', verticalAlign: 'middle' }}>{profile?.question_category_ids?.map(category => category.name).join(', ') || "-"}</td>
                                <td style={{ verticalAlign: 'middle' }}>
                                    <Button
                                    icon={<img src={EditIcon} style={{ width: 18, height: 18 }} />}
                                    minimal
                                    className="no-hover-btn"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openEditDrawer(profile); }}
                                    />
                                    <Button
                                    icon={<img src={DeleteIcon} style={{ width: 18, height: 18 }} />}
                                    minimal
                                    className="no-hover-btn"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); openDeleteDialog(profile); }}
                                    />
                                   
                                </td>
                            </tr>
                        ))}
                        {!isLoading && profiles.length === 0 && (
                            <tr>
                                <td
                                    className="table-header-text"
                                    colSpan={8}
                                    style={{ textAlign: "center", padding: "16px" }}
                                >
                                    No data found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </Flexbox>

            <ConfirmationDialog
                isOpen={isDeleteDialogOpen}
                onClose={closeDeleteDialog}
                onConfirm={confirmDeleteJobProfile}
                title="Delete job profile"
                message="Are you sure you want to delete"
                itemName={deleteTarget?.name || "this job profile"}
                confirmText="Yes, Delete"
                confirmIntent="danger"
                isSubmitting={isSubmitting}
            />

            {isDrawerOpen && (
                <>
                    <div
                        className="job-profile-drawer-overlay"
                        onClick={closeDrawer}
                    />

                    <div className="job-profile-drawer">
                        <div className={Classes.DRAWER_HEADER}>
                            <div className="job-profile-drawer-header">
                                <span className="job-profile-drawer-title">
                                    {isEditMode ? "Edit job profile" : "Add job profile"}
                                </span>

                                <div className="job-profile-drawer-actions">
                                    <FilledButton
                                        text={isEditMode ? "Save changes" : "Save"}
                                        onClick={handleSubmit}
                                        disabled={isSubmitting}
                                    />

                                    <Button minimal onClick={closeDrawer}>
                                        ✕
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className={Classes.DRAWER_BODY}>
                            <div className={Classes.DIALOG_BODY}>
                                <form onSubmit={handleSubmit}>
                                    <FormGroup
                                        label="Name"
                                        labelFor="job-profile-name"
                                        helperText={error}
                                        intent={error ? "danger" : "none"}
                                    >
                                        <InputGroup
                                            id="job-profile-name"
                                            placeholder="Please enter job profile"
                                            value={name}
                                            onChange={e => {
                                                setName(e.target.value);
                                                if (error) setError("");
                                            }}
                                            intent={error ? "danger" : "none"}
                                            autoFocus
                                        />
                                    </FormGroup>

                                    <FormGroup label="Description" labelFor="job-profile-description">
                                        <textarea
                                            className="bp3-input bp3-textarea bp3-fill"
                                            id="job-profile-description"
                                            placeholder="Short description"
                                            rows={10}
                                            value={description}
                                            onChange={e => setDescription(e.target.value)}
                                            dir="auto"
                                        />
                                    </FormGroup>
                                    <FormGroup label="Departments" labelFor="job-profile-categories">
                                        <ScrollableDropdown
                                            width={300}
                                            style={{ overflow: "visible" }}
                                            label="Select Departments"
                                            items={categories}
                                            getKey={category => category.id}
                                            getLabel={category => category.name}
                                            value={selectedCategories}
                                            onSelect={category =>
                                                setSelectedCategories(prev => {
                                                    const alreadySelected = prev.some(
                                                        item => item.id === category.id
                                                    );
                                                    if (alreadySelected) {
                                                        ToastStore?.showToast?.("Department already selected", "warning");
                                                        return prev;
                                                    }
                                                    return [...prev, category];
                                                })
                                            }
                                        />

                                    </FormGroup>

                                    <div style={{  display: "flex", flexWrap: "wrap", gap: "8px"}}
                                    >
                                        {selectedCategories.map(category => (
                                            <div
                                                key={category.id}
                                                style={{
                                                    background: "#EAF2FF",
                                                    color: "#2D72D9",
                                                    border: "1px solid #C5D8FF",
                                                    borderRadius: "20px",
                                                    padding: "6px 10px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "6px"
                                                }}
                                            >
                                                <span>{category.name}</span>

                                                <Icon
                                                    icon="cross"
                                                    iconSize={10}
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() =>
                                                        setSelectedCategories(prev =>
                                                            prev.filter(item => item.id !== category.id)
                                                        )
                                                    }
                                                />
                                            </div>
                                        ))}
                                    </div>
                                   
                                </form>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </Flexbox>
    );
};

export default UsersJobProfile;